import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';

// ----------------------------------------------------------------------
// Lazy-loaded page components — each page is a separate chunk
// ----------------------------------------------------------------------

const DashboardAppPage = lazy(() => import('./pages/DashboardAppPage'));
const UserPage = lazy(() => import('./pages/UserPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Page404 = lazy(() => import('./pages/Page404'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const Products = lazy(() => import('./pages/Products'));
const Category = lazy(() => import('./pages/Category'));
const Brand = lazy(() => import('./pages/Brand'));
const SubCategory = lazy(() => import('./pages/SubCategory'));
const Plan = lazy(() => import('./pages/Plan'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Memberships = lazy(() => import('./pages/Membership'));
const CategoryMSI = lazy(() => import('./pages/Category-MSI'));
const ProductMSI = lazy(() => import('./pages/Products-MSI'));
const ProductsFip = lazy(() => import('./pages/ProductsFip'));
const CategoryFip = lazy(() => import('./pages/CategoryFip'));
const SubCategoryFip = lazy(() => import('./pages/SubCategoryFip'));
const DeleteRequests = lazy(() => import('./pages/DeleteRequests'));
const ProductsManagement = lazy(() => import('./pages/ProductsManagement'));
const NotificationsDashboard = lazy(() => import('./pages/notifications/NotificationsDashboard'));
const NotificationsCompose   = lazy(() => import('./pages/notifications/NotificationsCompose'));
const NotificationsCampaigns = lazy(() => import('./pages/notifications/NotificationsCampaigns'));
const NotificationsScheduled = lazy(() => import('./pages/notifications/NotificationsScheduled'));
const NotificationsDevices   = lazy(() => import('./pages/notifications/NotificationsDevices'));
const NotificationsLogs      = lazy(() => import('./pages/notifications/NotificationsLogs'));
const NotificationCenterPage = lazy(() => import('./pages/notifications/NotificationCenterPage'));
const SearchAnalytics        = lazy(() => import('./pages/SearchAnalytics'));
const HomePageLayout = lazy(() => import('./pages/home/home'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

// ----------------------------------------------------------------------

function PageLoader() {
  return null;
}

// ----------------------------------------------------------------------

export default function Router() {
  const isAuthenticated = Boolean(localStorage.getItem('token'));

  const routes = useRoutes([
    {
      path: '/',
      element: (
        <Suspense fallback={<PageLoader />}>
          <HomePageLayout />
        </Suspense>
      ),
      children: [{ index: true, element: <Navigate to="/" /> }],
    },
    {
      path: '/privacypolicy',
      element: (
        <Suspense fallback={<PageLoader />}>
          <PrivacyPolicy />
        </Suspense>
      ),
    },
    {
      path: 'admin',
      element: isAuthenticated ? (
        <DashboardLayout />
      ) : (
        <Navigate to="/admin/login" replace />
      ),
      children: [
        { index: true, element: <Navigate to="/admin/dashboard/app" replace /> },
        {
          path: 'dashboard/app',
          element: (
            <Suspense fallback={<PageLoader />}>
              <DashboardAppPage />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/user',
          element: (
            <Suspense fallback={<PageLoader />}>
              <UserPage />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/product',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Products />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/category',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Category />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/brand',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Brand />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/products',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ProductsPage />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/blog',
          element: (
            <Suspense fallback={<PageLoader />}>
              <BlogPage />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/subCategory',
          element: (
            <Suspense fallback={<PageLoader />}>
              <SubCategory />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/plans',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Plan />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/membership',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Memberships />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/changePassword',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ChangePassword />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/category-msi',
          element: (
            <Suspense fallback={<PageLoader />}>
              <CategoryMSI />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/product-msi',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ProductMSI />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/product-fip',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ProductsFip />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/category-fip',
          element: (
            <Suspense fallback={<PageLoader />}>
              <CategoryFip />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/sub-category-fip',
          element: (
            <Suspense fallback={<PageLoader />}>
              <SubCategoryFip />
            </Suspense>
          ),
        },
        // Legacy route — redirect to new integrated module
        {
          path: 'dashboard/SendNotification',
          element: <Navigate to="/admin/dashboard/notifications/dashboard" replace />,
        },
        {
          path: 'dashboard/delete-requests',
          element: (
            <Suspense fallback={<PageLoader />}>
              <DeleteRequests />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/products-management',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ProductsManagement />
            </Suspense>
          ),
        },
        // Push Notifications module
        {
          path: 'dashboard/notification-center',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationCenterPage />
            </Suspense>
          ),
        },
        { path: 'dashboard/notifications', element: <Navigate to="/admin/dashboard/notifications/dashboard" replace /> },
        {
          path: 'dashboard/notifications/dashboard',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationsDashboard />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/notifications/compose',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationsCompose />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/notifications/campaigns',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationsCampaigns />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/notifications/scheduled',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationsScheduled />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/notifications/devices',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationsDevices />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/notifications/logs',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotificationsLogs />
            </Suspense>
          ),
        },
        {
          path: 'dashboard/search-analytics',
          element: (
            <Suspense fallback={<PageLoader />}>
              <SearchAnalytics />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'admin/login',
      element: isAuthenticated ? (
        <Navigate to="/admin/dashboard/app" replace />
      ) : (
        <Suspense fallback={<PageLoader />}>
          <LoginPage />
        </Suspense>
      ),
    },
    {
      element: <SimpleLayout />,
      children: [
        { index: true, element: <Navigate to="/admin/dashboard/app" /> },
        {
          path: '404',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Page404 />
            </Suspense>
          ),
        },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
